import {
  Category,
  CategoryAggsResponse,
  CategoryConfigResponse,
  CategoryDesktop,
  CategoryFilterResultResponse,
  CategoryProductResponse,
} from '@models/category';
import {
  CommonResponse,
  PageCommonResponse,
  PageInfoResponse,
} from '@models/common';
import { DisplayAreaInfo } from '@models/display';
import {
  MolocoAdProductRespose,
  RequestMolocoAdCategoryProductsParam,
} from '@models/moloco';
import { get, post } from '@apis/api';
import { getCookieWckDisplayMktSID } from '@wconcept/wconcept-common/utils/cookie';
import { getMolocoCustNo } from '@utils/domainUtil';
import type { CommonResponse as ApiCommonResponse } from '@models/api';
import type {
  LpCategoryPath,
  LpProductFilterType,
  LpProductRequest,
} from '@apis/renewal/lpProduct';
import { ApiTypes } from './apiType';

const baseURL = process.env.NEXT_PUBLIC_DISPLAY_API_BASE_URL;
const options = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8', // 필요에 따라 헤더 수정
    'DISPLAY-API-KEY': process.env.NEXT_PUBLIC_DISPLAY_API_KEY || '',
  },
};
const gatewayBaseURL = process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_URL;
const gatewayOptions = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'DISPLAY-API-KEY': process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_KEY || '',
  },
};

const getCategoryLpProductPath = ({
  mediumCd,
  largeCategory,
  middleCategory,
  smallCategory,
}: LpCategoryPath): string => {
  const categoryPath = `${mediumCd}/${largeCategory}`;

  if (!middleCategory) return categoryPath;
  if (!smallCategory) return `${categoryPath}/${middleCategory}`;

  return `${categoryPath}/${middleCategory}/${smallCategory}`;
};

/**
 * 카테고리 LP 상품 API v1
 * 초기 데이터, 상품 목록, 상품 수, 필터를 조회한다.
 */
const postCategoryLpProduct = async <TResponse>(
  url: string,
  params: LpProductRequest
): Promise<TResponse | undefined> => {
  const { data } = await post<ApiCommonResponse<TResponse>, LpProductRequest>({
    url,
    baseUrl: gatewayBaseURL,
    options: gatewayOptions,
    data: params,
  });

  return data;
};

export const requestCategoryLpProductInit = async <TResponse>(
  path: LpCategoryPath,
  params: LpProductRequest
): Promise<TResponse | undefined> =>
  postCategoryLpProduct(
    `/display/api/category/v1/products/init/${getCategoryLpProductPath(path)}`,
    params
  );

export const requestCategoryLpProduct = async <TResponse>(
  path: LpCategoryPath,
  params: LpProductRequest
): Promise<TResponse | undefined> =>
  postCategoryLpProduct(
    `/display/api/category/v1/products/${getCategoryLpProductPath(path)}`,
    params
  );

export const requestCategoryLpProductCount = async <TResponse>(
  path: LpCategoryPath,
  params: LpProductRequest
): Promise<TResponse | undefined> =>
  postCategoryLpProduct(
    `/display/api/category/v1/products/count/${getCategoryLpProductPath(path)}`,
    params
  );

export const requestCategoryLpProductFilter = async <TResponse>(
  path: LpCategoryPath,
  type: LpProductFilterType,
  params: LpProductRequest
): Promise<TResponse | undefined> =>
  postCategoryLpProduct(
    `/display/api/category/v1/products/filter/${type}/${getCategoryLpProductPath(
      path
    )}`,
    params
  );
/**
 *  카테고리 Config 조회
 */
export const GET_CATEGORY_CONFIG_API = () =>
  `/display/api/v1/category/search/config`;

export const requestCategoryConfig = async () => {
  const { data } = await get<CategoryConfigResponse>({
    url: GET_CATEGORY_CONFIG_API(),
    baseUrl: baseURL,
    options,
  });
  // const { data } = await axiosDisplay.get<CategoryConfigResponse>(
  //   GET_CATEGORY_CONFIG_API()
  // );
  return data;
};

/**
 * Category 조회.
 * v1 > v2로 변경 (kids 추가)
 */
export interface CategoryResponse extends CommonResponse {
  data: Category;
}

export const GET_CATEGORY_API = (
  categoryType: string,
  gender: string,
  category: string,
  shopcd?: string
) =>
  `/display/api/v2/category/list?categoryType=${categoryType}&gender=${gender}&category=${category}&shopcd=${shopcd}`;

export const requestCategory = async (
  categoryType: string,
  gender: string,
  category: string,
  shopcd?: string
) => {
  const { data } = await get<CategoryResponse>({
    url: GET_CATEGORY_API(
      categoryType,
      gender,
      category,
      shopcd === undefined ? '' : shopcd
    ),
    baseUrl: baseURL,
    options,
  });

  // const { data } = await axiosDisplay.get<CategoryResponse>(
  //   GET_CATEGORY_API(
  //     categoryType,
  //     gender,
  //     category,
  //     shopcd === undefined ? '' : shopcd
  //   )
  // );
  return data;
};

/**
 * Category PC 조회.
 * v1 > v2로 변경 (kids 추가)
 */
export interface CategoryDesktopResponse extends CommonResponse {
  data: CategoryDesktop;
}

export const GET_CATEGORY_PC_API = (
  categoryType: string,
  gender: string,
  category: string,
  shopcd?: string
) =>
  `/display/api/v2/category/list/pc?categoryType=${categoryType}&gender=${gender}&category=${category}&shopcd=${shopcd}`;
export const requestCategoryDesktop = async (
  categoryType: string,
  gender: string,
  category: string,
  shopcd?: string
) => {
  const { data } = await get<CategoryDesktopResponse>({
    url: GET_CATEGORY_PC_API(
      categoryType,
      gender,
      category,
      shopcd === undefined ? '' : shopcd
    ),
    baseUrl: baseURL,
    options,
  });

  // const { data } = await axiosDisplay.get<CategoryDesktopResponse>(
  //   GET_CATEGORY_PC_API(
  //     categoryType,
  //     gender,
  //     category,
  //     shopcd === undefined ? '' : shopcd
  //   )
  // );
  return data;
};

/**
 * Category Display 조회.
 */
export interface CategoryDisplayRequestParam {
  custNo: string;
  displayDate: string | null;
  pageNo: number;
  pageSize: number;
  domainType: 'm' | 'pc'; // 모바일 : m , PC : pc
  recommendItems: string[];
  categoryType: string;
  category: string;
  gender: string;
}

export interface MainDisplayResponse extends PageCommonResponse {
  data: MainDisplayContent;
}
export interface MainDisplayContent extends PageInfoResponse {
  content: DisplayAreaInfo[];
}
// eslint-disable-next-line @typescript-eslint/naming-convention
export function GET_CATEGORY_DISPLAY_API() {
  return `/display/api/v1/display/category`;
}

export const requestCategoryDisplay = async (
  body: CategoryDisplayRequestParam,
  pageParam: number,
  pageSize: number
) => {
  const modifiedBody = { ...body }; // Create a copy of the 'body' object
  modifiedBody.pageNo = pageParam === null ? 0 : pageParam;
  if (modifiedBody.pageNo === 0) {
    modifiedBody.pageNo = 1;
    modifiedBody.pageSize = pageSize;
  } else if (modifiedBody.pageNo === 1) {
    modifiedBody.pageNo = 2;
    modifiedBody.pageSize = 6;
  }
  const { data } = await get<MainDisplayResponse, CategoryDisplayRequestParam>({
    url: GET_CATEGORY_DISPLAY_API(),
    baseUrl: baseURL,
    options,
    params: modifiedBody,
  });

  // const { data } = await axiosDisplay.post<MainDisplayResponse>(
  //   GET_CATEGORY_DISPLAY_API(),
  //   modifiedBody // Use the modified copy of 'body'
  // );
  return data;
};

/**
 * Category 상품리스트 조회.
 */

export const GET_CATEGORY_PRODUCT_API = ({
  mediumCd,
  largeCategory,
  middleCategory,
  smallCategory,
}: {
  mediumCd: string;
  largeCategory: string;
  middleCategory?: string;
  smallCategory?: string;
}) => {
  if (middleCategory) {
    if (smallCategory) {
      return `${ApiTypes.CATEGORY}/products/${mediumCd}/${largeCategory}/${middleCategory}/${smallCategory}`;
    }
    return `${ApiTypes.CATEGORY}/products/${mediumCd}/${largeCategory}/${middleCategory}`;
  }
  return `${ApiTypes.CATEGORY}/products/${mediumCd}/${largeCategory}`;
};

export const requestCategoryProductResult = async ({
  mediumCd,
  largeCategory,
  middleCategory,
  smallCategory,
  custNo,
  gender,
  sort,
  pageNo,
  pageSize,
  bcds,
  priceSections,
  minPrice,
  maxPrice,
  colors,
  benefits,
  discounts,
  status,
  saleTags,
  shopCds,
  domainType,
}: RequestCategoryProdcutListParams) => {
  const { data } = await post<CategoryProductResponse>({
    url: GET_CATEGORY_PRODUCT_API({
      mediumCd,
      largeCategory,
      middleCategory,
      smallCategory,
    }),
    baseUrl: baseURL,
    options,
    data: {
      custNo,
      gender,
      sort,
      pageNo,
      pageSize,
      bcds,
      priceSections,
      minPrice,
      maxPrice,
      colors,
      benefits,
      discounts,
      status,
      saleTags,
      shopCds,
      domainType,
    },
  });

  // const { data } = await axiosDisplay.post<CategoryProductResponse>(
  //   GET_CATEGORY_PRODUCT_API({
  //     mediumCd,
  //     middleCategory,
  //     smallCategory,
  //     largeCategory,
  //   }),
  //   {
  //     custNo,
  //     gender,
  //     sort,
  //     pageNo,
  //     pageSize,
  //     bcds,
  //     priceSections,
  //     minPrice,
  //     maxPrice,
  //     colors,
  //     benefits,
  //     discounts,
  //     status,
  //     saleTags,
  //     shopCds,
  //     domainType,
  //   }
  // );
  return data;
};

/**
 * 결과 - 필터 (집계정보)
 */
export interface RequestCategoryProdcutListParams {
  mediumCd: string;
  largeCategory: string;
  middleCategory?: string;
  smallCategory?: string;
  gender: string;
  custNo: string;
  sort?: string;
  pageNo?: number;
  pageSize?: number;
  bcds?: string[];
  priceSections?: number[];
  minPrice?: number;
  maxPrice?: number;
  colors?: string[];
  benefits?: string[];
  discounts?: string[];
  status?: string[];
  saleTags?: string[];
  shopCds?: string[];
  domainType?: string;
}

/**
 * 카테고리 상품 결과값 -> requestCategoryProductAgg()
 */
export interface RequestCategoryAccParams {
  custNo?: string;
  gender?: string;
  sort?: string;
  pageNo?: number;
  pageSize?: number;
  bcds?: string[];
  priceSections?: null;
  minPrice?: string | number;
  maxPrice?: string | number;
  colors?: string[];
  benefits?: string[];
  discounts?: string[];
  status?: string[];
  saleTags?: string[];
  aggs?: string[];
  shopCds?: string[];
  domainType?: string;
}

/**
 * 카테고리 필터
 */
export const GET_CATEGORY_PRODUCT_AGGS = ({
  mediumCd,
  largeCategory,
  middleCategory,
  smallCategory,
}: {
  mediumCd: string;
  largeCategory: string;
  middleCategory?: string;
  smallCategory?: string;
}) => {
  if (middleCategory) {
    if (smallCategory) {
      return `${ApiTypes.CATEGORY}/products/aggs/${mediumCd}/${largeCategory}/${middleCategory}/${smallCategory}`;
    }
    return `${ApiTypes.CATEGORY}/products/aggs/${mediumCd}/${largeCategory}/${middleCategory}`;
  }
  return `${ApiTypes.CATEGORY}/products/aggs/${mediumCd}/${largeCategory}`;
};

export const requstCategoryProductAggs = async (
  {
    mediumCd,
    largeCategory,
    middleCategory,
    smallCategory,
  }: {
    mediumCd: string;
    largeCategory: string;
    middleCategory?: string;
    smallCategory?: string;
  },

  {
    custNo,
    gender,
    sort,
    pageNo = 1,
    pageSize,
    bcds,
    priceSections,
    minPrice,
    maxPrice,
    colors,
    benefits,
    discounts,
    status,
    saleTags,
    shopCds,
    domainType,
    aggs = [
      'brand',
      'topBrand',
      'newBrand',
      'category',
      'status',
      'discounts',
      'benefits',
      'recoCategory',
      'colors',
      'saleTag',
      'priceRange',
    ],
  }: RequestCategoryAccParams
) => {
  const { data } = await post<CategoryAggsResponse>({
    url: GET_CATEGORY_PRODUCT_AGGS({
      mediumCd,
      largeCategory,
      middleCategory,
      smallCategory,
    }),
    baseUrl: baseURL,
    options,
    data: {
      custNo,
      gender,
      sort,
      pageNo,
      pageSize,
      bcds,
      priceSections,
      minPrice,
      maxPrice,
      colors,
      benefits,
      discounts,
      status,
      saleTags,
      shopCds,
      aggs,
      domainType,
    },
  });
  // const { data } = await axiosDisplay.post<CategoryAggsResponse>(
  //   GET_CATEGORY_PRODUCT_AGGS({
  //     mediumCd,
  //     largeCategory,
  //     middleCategory,
  //     smallCategory,
  //   }),
  //   {
  //     custNo,
  //     gender,
  //     sort,
  //     pageNo,
  //     pageSize,
  //     bcds,
  //     priceSections,
  //     minPrice,
  //     maxPrice,
  //     colors,
  //     benefits,
  //     discounts,
  //     status,
  //     saleTags,
  //     shopCds,
  //     aggs,
  //     domainType,
  //   }
  // );
  return data;
};

/**
 * 카테고리 필터 다른 버전
 */

export const GET_CATEGORY_RESULTS = ({
  mediumCd,
  largeCategory,
  middleCategory,
  smallCategory,
}: {
  mediumCd: string;
  largeCategory: string;
  middleCategory?: string;
  smallCategory?: string;
}) => {
  if (middleCategory) {
    if (smallCategory) {
      return `${ApiTypes.CATEGORY}/products/result/${mediumCd}/${largeCategory}/${middleCategory}/${smallCategory}`;
    }
    return `${ApiTypes.CATEGORY}/products/result/${mediumCd}/${largeCategory}/${middleCategory}`;
  }
  return `${ApiTypes.CATEGORY}/products/result/${mediumCd}/${largeCategory}`;
};

export const requestCategoryResultProduct = async (
  {
    mediumCd,
    largeCategory,
    middleCategory,
    smallCategory,
  }: {
    mediumCd: string;
    largeCategory: string;
    middleCategory?: string;
    smallCategory?: string;
  },
  {
    custNo,
    gender,
    sort,
    pageNo = 1,
    pageSize,
    bcds,
    priceSections,
    minPrice,
    maxPrice,
    colors,
    benefits,
    discounts,
    status,
    shopCds,
    domainType,
    saleTags,
    aggs = [
      'brand',
      'topBrand',
      'newBrand',
      'category',
      'status',
      'discounts',
      'benefits',
      'recoCategory',
      'colors',
      'saleTag',
      'priceRange',
    ],
  }: RequestCategoryAccParams
) => {
  const { data } = await post<CategoryFilterResultResponse>({
    url: GET_CATEGORY_RESULTS({
      mediumCd,
      largeCategory,
      middleCategory,
      smallCategory,
    }),
    baseUrl: baseURL,
    options,
    data: {
      custNo,
      gender,
      sort,
      pageNo,
      pageSize,
      bcds,
      priceSections,
      minPrice,
      maxPrice,
      colors,
      benefits,
      discounts,
      status,
      saleTags,
      aggs,
      shopCds,
      domainType,
    },
  });

  // const { data } = await axiosDisplay.post<CategoryFilterResultResponse>(
  //   GET_CATEGORY_RESULTS({
  //     mediumCd,
  //     largeCategory,
  //     middleCategory,
  //     smallCategory,
  //   }),
  //   {
  //     custNo,
  //     gender,
  //     sort,
  //     pageNo,
  //     pageSize,
  //     bcds,
  //     priceSections,
  //     minPrice,
  //     maxPrice,
  //     colors,
  //     benefits,
  //     discounts,
  //     status,
  //     saleTags,
  //     aggs,
  //     shopCds,
  //     domainType,
  //   }
  // );
  return data;
};

/**
 * 카테고리 몰로코 API
 */

export const GET_CATEGORY_MOLOCO_RESULTS = ({
  mediumCd,
  largeCategory,
  middleCategory,
}: {
  mediumCd: string;
  largeCategory: string;
  middleCategory: string;
}) => {
  if (middleCategory !== '') {
    return `/display/api/v1/moloco/category/ad/products/${mediumCd}/${largeCategory}/${middleCategory}`;
  }
  return `/display/api/v1/moloco/category/ad/products/${mediumCd}/${largeCategory}`;
};

export const requestMolocoAdProductList = async ({
  id,
  pageId,
  // sessionId,
  // userId,
  categoryName,
  mediumCd,
  largeCategory,
  middleCategory,
}: RequestMolocoAdCategoryProductsParam) => {
  const sessionId = await getCookieWckDisplayMktSID();
  const userId = await getMolocoCustNo(false);

  try {
    const { data } = await post<MolocoAdProductRespose>({
      url: GET_CATEGORY_MOLOCO_RESULTS({
        mediumCd,
        largeCategory,
        middleCategory,
      }),
      baseUrl: baseURL,
      options,
      data: {
        id,
        pageId,
        sessionId: sessionId ? encodeURIComponent(sessionId) : '',
        userId,
        categoryName,
      },
    });
    // const { data } = await axiosDisplay.post<MolocoAdProductRespose>(
    //   GET_CATEGORY_MOLOCO_RESULTS({ mediumCd, largeCategory, middleCategory }),
    //   // `/display/api/v1/moloco/category/ad/products/${categoryType}/${largeCategory}`,
    //   {
    //     id,
    //     pageId,
    //     sessionId,
    //     userId,
    //     categoryName,
    //   }
    // );
    return data;
  } catch (e: any) {
    return {
      result: 'error',
      errorCode: e?.status,
      message: '',
      data: {
        webPosition: 0,
        appPosition: 0,
        items: [],
        total: 0,
      },
    } as MolocoAdProductRespose;
  }
};
